// pages/master/company/MasterCompanyAddEdit.jsx
'use client';

import { useState, useEffect, useMemo } from 'react';
import { Box, Grid, TextField, CircularProgress, Button, FormControlLabel, Switch } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import PickerField from '../../../components/PickerField';
import FormDialog from '../../../components/FormDialog';
import { useNotifier } from '../../../components/Notifier';
import { useLanguage } from '../../../utils/i18n';
import { createCompany, updateCompany, getCompanyById } from '../../../stores/companyStore';
import { useBusinessTypeList } from '../../../stores/businessTypeStore';
import { formatUpperCase } from '../../../utils/formatTitleCase';

const MasterCompanyAddEdit = ({ open, id = null, onClose, onSuccess }) => {
  const notify = useNotifier();
  const { t } = useLanguage();

  const [initialValues, setInitialValues] = useState({
    name: '',
    code: '',
    business_type: null,
    business_type_name: '',
    city: '',
    country: '', // Teks biasa, bukan picker
    address: '',
    zip_code: '',
    state: '',
    phone1: '',
    phone2: '',
    fax1: '',
    fax2: '',
    pic_name: '',
    pic_email: '',
    pic_phone_no: '',
    pic_mobile_no: '',
    status: true
  });

  const [loading, setLoading] = useState(false);

  const { data: businessTypeList = [] } = useBusinessTypeList(0, 1000, '');

  const businessTypeOptions = useMemo(
    () =>
      (businessTypeList || []).map((v) => ({
        value: v?.id != null ? String(v.id) : String(v?.value ?? ''),
        label: v?.name ?? v?.business_type_name ?? String(v?.id ?? v?.value ?? '')
      })),
    [businessTypeList]
  );
  useEffect(() => {
    if (!open) return;

    let mounted = true;
    const fetch = async () => {
      if (!id) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const data = await getCompanyById(id);
        if (!mounted) return;
        if (data) {
          const businessTypeVal = data.business_type ?? data.business_type_id ?? null;

          setInitialValues((prev) => ({
            ...prev,
            name: data.name ?? prev.name,
            code: data.code ?? prev.code,
            business_type: businessTypeVal != null ? String(businessTypeVal) : prev.business_type,
            business_type_name: data.business_type_name ?? data.join?.business_type_name ?? prev.business_type_name,
            city: data.city ?? prev.city,
            country: data.country ?? prev.country,
            address: data.address ?? prev.address,
            zip_code: data.zip_code ?? prev.zip_code,
            state: data.state ?? prev.state,
            phone1: data.phone1 ?? prev.phone1,
            phone2: data.phone2 ?? prev.phone2,
            fax1: data.fax1 ?? prev.fax1,
            fax2: data.fax2 ?? prev.fax2,
            pic_name: data.pic_name ?? prev.pic_name,
            pic_email: data.pic_email ?? prev.pic_email,
            pic_phone_no: data.pic_phone_no ?? prev.pic_phone_no,
            pic_mobile_no: data.pic_mobile_no ?? prev.pic_mobile_no,
            status: Boolean(data.status)
          }));
        }
      } catch (err) {
        console.error('Failed to load company:', err);
        notify({ message: t('company.failedLoadData', 'Failed to load company data'), severity: 'error' });
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetch();
    return () => {
      mounted = false;
    };
  }, [id, open, notify, t]);

  // Validasi formik dengan batasan min/max
  const validationSchema = Yup.object().shape({
    name: Yup.string().required(t('company.validationCompanyNameRequired', 'Company Name is required')),
    business_type: Yup.mixed().required(t('company.validationBusinessTypeRequired', 'Business Type is required')),
    code: Yup.string()
      .test('min', t('company.validationMin2', 'Minimum 2 characters'), (val) => !val || val.length >= 2)
      .max(10, t('company.validationMax10', 'Maximum 10 characters')),
    zip_code: Yup.string()
      .required(t('company.validationZipCodeRequired', 'Zip Code is required'))
      .test('min', t('company.validationMin5', 'Minimum 5 characters'), (val) => !val || val.length >= 5),
    phone1: Yup.string()
      .required(t('company.validationPhone1Required', 'Phone 1 is required'))
      .test('min', t('company.validationMin10', 'Minimum 10 characters'), (val) => !val || val.length >= 10)
      .max(15, t('company.validationMax15', 'Maximum 15 characters')),
    fax1: Yup.string()
      .test('min', t('company.validationMin10', 'Minimum 10 characters'), (val) => !val || val.length >= 10)
      .max(15, t('company.validationMax15', 'Maximum 15 characters')),
    fax2: Yup.string()
      .test('min', t('company.validationMin10', 'Minimum 10 characters'), (val) => !val || val.length >= 10)
      .max(15, t('company.validationMax15', 'Maximum 15 characters')),
    pic_phone_no: Yup.string()
      .test('min', t('company.validationMin10', 'Minimum 10 characters'), (val) => !val || val.length >= 10)
      .max(15, t('company.validationMax15', 'Maximum 15 characters')),
    pic_mobile_no: Yup.string()
      .test('min', t('company.validationMin10', 'Minimum 10 characters'), (val) => !val || val.length >= 10)
      .max(15, t('company.validationMax15', 'Maximum 15 characters'))
  });
  const handleClose = () => {
    formik.resetForm();
    if (onClose) onClose();
  };

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const numericOrNull = (v) => {
          if (v == null || v === '') return null;
          const n = Number(v);
          return Number.isNaN(n) ? v : n;
        };

        const payload = {
          id: id ?? 0,
          name: formatUpperCase(values.name) ?? '',
          code: values.code ?? '', // MIN 2 MAX 10
          business_type: numericOrNull(values.business_type),
          city: values.city ?? '',
          country: values.country ?? '',
          address: values.address ?? '',
          zip_code: values.zip_code ?? '', // MIN 5
          owner_mobile_no: '0000000000',
          state: values.state ?? '',
          phone1: values.phone1 ?? '', // MIN 10 MAX 15
          phone2: values.phone2 ?? '',
          fax1: values.fax1 ?? '', // MIN 10 MAX 15
          fax2: values.fax2 ?? '', // MIN 10 MAX 15
          pic_name: values.pic_name ?? '',
          pic_email: values.pic_email ?? '',
          pic_phone_no: values.pic_phone_no ?? '', // MIN 10 MAX 15
          pic_mobile_no: values.pic_mobile_no ?? '', // MIN 10 MAX 15
          status: Boolean(values.status)
        };

        if (id) {
          await updateCompany(id, payload);
          notify({ message: t('company.updateSuccess', 'Company updated successfully'), severity: 'success' });
        } else {
          await createCompany(payload);
          notify({ message: t('company.createSuccess', 'Company added successfully'), severity: 'success' });
        }

        formik.resetForm();
        if (onSuccess) onSuccess();
        if (onClose) onClose();
      } catch (err) {
        console.error('Submit error:', err);
        const errorMessage = err?.response?.data?.message || err?.data?.message || err?.message || 'Gagal menyimpan data';
        notify({ message: errorMessage, severity: 'error' });
      } finally {
        setSubmitting(false);
      }
    }
  });

  const getOptionId = (o) => (o == null ? o : (o.value ?? o.id ?? o));
  const getOptionLabel = (o) => (o == null ? '' : (o.label ?? o.name ?? String(o.value ?? o.id ?? o)));

  const findOptionByValue = (options = [], value) => {
    if (value == null || value === '') return null;
    return (
      (options || []).find((o) => {
        try {
          return String(getOptionId(o)) === String(value);
        } catch {
          return false;
        }
      }) ?? null
    );
  };

  const dialogActions = (
    <>
      <Button onClick={handleClose} disabled={formik.isSubmitting} sx={{ color: 'text.secondary', textTransform: 'none' }}>
        {t('buttonTitle.cancel', 'Cancel')}
      </Button>
      <Button
        variant="contained"
        color="primary"
        onClick={formik.handleSubmit}
        disabled={formik.isSubmitting || loading}
        startIcon={formik.isSubmitting ? <CircularProgress size={18} color="inherit" /> : null}
        sx={{ textTransform: 'none', borderRadius: 1, px: 3, fontWeight: 700 }}
      >
        {formik.isSubmitting ? t('buttonTitle.processing', 'Processing...') : t('buttonTitle.save', 'Save')}
      </Button>
    </>
  );

  return (
    <FormDialog
      open={open}
      onClose={handleClose}
      title={
        id
          ? t('general.edit', 'Edit') + ' ' + t('general.company', 'Company')
          : t('general.add', 'Add') + ' ' + t('general.company', 'Company')
      }
      maxWidth="md"
      fullScreenMode={false}
      actions={dialogActions}
    >
      <Box sx={{ width: '100%', pt: 1 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={4}>
              <Grid item size={{ xs: 12, md: 6 }}>
                <Grid container spacing={2.5}>
                  <Grid item size={{ xs: 12 }}>
                    <TextField
                      label={t('company.name', 'Company Name') + ' *'}
                      name="name"
                      value={formik.values.name}
                      onChange={(e) => {
                        formik.setFieldValue('name', formatUpperCase(e.target.value));
                      }}
                      onBlur={formik.handleBlur}
                      size="medium"
                      fullWidth
                      error={formik.touched.name && Boolean(formik.errors.name)}
                      helperText={formik.touched.name && formik.errors.name}
                    />
                  </Grid>

                  <Grid item size={{ xs: 12 }}>
                    <TextField
                      label={t('company.code', 'Company Code') + ' *'}
                      name="code"
                      value={formik.values.code}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      size="medium"
                      fullWidth
                      error={formik.touched.code && Boolean(formik.errors.code)}
                      helperText={formik.touched.code && formik.errors.code}
                    />
                  </Grid>

                  <Grid item size={{ xs: 12 }}>
                    <PickerField
                      label={t('company.businessType', 'Business Type') + ' *'}
                      value={formik.values.business_type}
                      options={businessTypeOptions}
                      onChange={(opt) => {
                        const selected = findOptionByValue(businessTypeOptions, opt) ?? (typeof opt === 'object' ? opt : null);
                        const idVal = selected ? getOptionId(selected) : (opt ?? null);
                        formik.setFieldValue('business_type', idVal);
                        if (selected) formik.setFieldValue('business_type_name', getOptionLabel(selected));
                      }}
                      getOptionId={(o) => getOptionId(o)}
                      getOptionLabel={(o) => getOptionLabel(o)}
                      dialogTitle={t('masterCompany.selectBusinessType', 'Select Business Type')}
                      error={formik.touched.business_type && !!formik.errors.business_type}
                      helperText={formik.touched.business_type && formik.errors.business_type}
                    />
                  </Grid>

                  <Grid item size={{ xs: 12 }}>
                    <TextField
                      label={t('company.country', 'Country') + ' *'}
                      name="country"
                      value={formik.values.country}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      size="medium"
                      fullWidth
                    />
                  </Grid>

                  <Grid item size={{ xs: 12 }}>
                    <TextField
                      label={t('company.state', 'State')}
                      name="state"
                      value={formik.values.state}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      size="medium"
                      fullWidth
                    />
                  </Grid>

                  <Grid item size={{ xs: 12 }}>
                    <TextField
                      label={t('company.city', 'City')}
                      name="city"
                      value={formik.values.city}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      size="medium"
                      fullWidth
                    />
                  </Grid>

                  <Grid item size={{ xs: 12 }}>
                    <TextField
                      label={t('company.address', 'Address')}
                      name="address"
                      value={formik.values.address}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      size="medium"
                      fullWidth
                      multiline
                      rows={2}
                    />
                  </Grid>

                  <Grid item size={{ xs: 12 }}>
                    <TextField
                      label={t('company.zipCode', 'ZIP Code')}
                      name="zip_code"
                      value={formik.values.zip_code}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      size="medium"
                      fullWidth
                      error={formik.touched.zip_code && Boolean(formik.errors.zip_code)}
                      helperText={formik.touched.zip_code && formik.errors.zip_code}
                    />
                  </Grid>

                  <Grid item size={{ xs: 12 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={Boolean(formik.values.status)}
                          onChange={(e) => formik.setFieldValue('status', e.target.checked)}
                          name="status"
                          color="primary"
                        />
                      }
                      label={t('masterCompany.statusActive', 'Status (Active)')}
                      sx={{ mt: 1 }}
                    />
                  </Grid>
                </Grid>
              </Grid>

              {/* KOLOM KANAN: Informasi Kontak & PIC */}
              <Grid item size={{ xs: 12, md: 6 }}>
                <Grid container spacing={2.5}>
                  <Grid item size={{ xs: 12 }}>
                    <TextField
                      label={t('masterCompany.phone1', 'Phone 1')}
                      name="phone1"
                      value={formik.values.phone1}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      size="medium"
                      fullWidth
                      error={formik.touched.phone1 && Boolean(formik.errors.phone1)}
                      helperText={formik.touched.phone1 && formik.errors.phone1}
                    />
                  </Grid>

                  <Grid item size={{ xs: 12 }}>
                    <TextField
                      label={t('masterCompany.phone2', 'Phone 2')}
                      name="phone2"
                      value={formik.values.phone2}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      size="medium"
                      fullWidth
                    />
                  </Grid>

                  <Grid item size={{ xs: 12 }}>
                    <TextField
                      label={t('masterCompany.fax1', 'Fax 1')}
                      name="fax1"
                      value={formik.values.fax1}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      size="medium"
                      fullWidth
                      error={formik.touched.fax1 && Boolean(formik.errors.fax1)}
                      helperText={formik.touched.fax1 && formik.errors.fax1}
                    />
                  </Grid>

                  <Grid item size={{ xs: 12 }}>
                    <TextField
                      label={t('masterCompany.fax2', 'Fax 2')}
                      name="fax2"
                      value={formik.values.fax2}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      size="medium"
                      fullWidth
                      error={formik.touched.fax2 && Boolean(formik.errors.fax2)}
                      helperText={formik.touched.fax2 && formik.errors.fax2}
                    />
                  </Grid>

                  <Grid item size={{ xs: 12 }}>
                    <TextField
                      label={t('company.picName', 'PIC Name')}
                      name="pic_name"
                      value={formik.values.pic_name}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      size="medium"
                      fullWidth
                    />
                  </Grid>

                  <Grid item size={{ xs: 12 }}>
                    <TextField
                      label={t('company.picEmail', 'PIC Email')}
                      name="pic_email"
                      type="email"
                      value={formik.values.pic_email}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      size="medium"
                      fullWidth
                    />
                  </Grid>

                  <Grid item size={{ xs: 12 }}>
                    <TextField
                      label={t('company.picPhone', 'PIC Phone')}
                      name="pic_phone_no"
                      value={formik.values.pic_phone_no}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      size="medium"
                      fullWidth
                      error={formik.touched.pic_phone_no && Boolean(formik.errors.pic_phone_no)}
                      helperText={formik.touched.pic_phone_no && formik.errors.pic_phone_no}
                    />
                  </Grid>

                  <Grid item size={{ xs: 12 }}>
                    <TextField
                      label={t('company.picMobileNo', 'PIC Mobile No')}
                      name="pic_mobile_no"
                      value={formik.values.pic_mobile_no}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      size="medium"
                      fullWidth
                      error={formik.touched.pic_mobile_no && Boolean(formik.errors.pic_mobile_no)}
                      helperText={formik.touched.pic_mobile_no && formik.errors.pic_mobile_no}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </form>
        )}
      </Box>
    </FormDialog>
  );
};

export default MasterCompanyAddEdit;
